import React, { useState, useEffect } from 'react';

const CustomCalendar = ({ 
  value, 
  onChange, 
  showTime = false, 
  placeholder = "Select date...",
  label,
  required = false,
  className = "",
  theme = "workflows" // Default theme
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);

  // Theme configuration for different apps
  const themes = {
    workflows: {
      primary: '#FF3B30',
      primaryHover: '#FF3B30',
      primaryBg: 'rgba(255,59,48,0.2)',
      border: '#FF3B30'
    },
    bookings: {
      primary: '#FFFF00',
      primaryHover: '#FFFF33',
      primaryBg: 'rgba(255,255,0,0.2)',
      border: '#FFFF00'
    },
    catalog: {
      primary: '#17A2B8',
      primaryHover: '#138496',
      primaryBg: 'rgba(23,162,184,0.2)',
      border: '#17A2B8'
    },
    tasks: {
      primary: '#FF6B35',
      primaryHover: '#e55a2b',
      primaryBg: 'rgba(255,107,53,0.2)',
      border: '#FF6B35'
    }
  };

  const currentTheme = themes[theme] || themes.workflows;
  const [selectedTime, setSelectedTime] = useState(
    value ? {
      hours: new Date(value).getHours().toString().padStart(2, '0'),
      minutes: new Date(value).getMinutes().toString().padStart(2, '0')
    } : { hours: '12', minutes: '00' }
  );

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      setCurrentDate(date);
      if (showTime) {
        setSelectedTime({
          hours: date.getHours().toString().padStart(2, '0'),
          minutes: date.getMinutes().toString().padStart(2, '0')
        });
      }
    }
  }, [value, showTime]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDisplayDate = (date) => {
    if (!date) return placeholder;
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    };
    let formatted = date.toLocaleDateString('en-US', options);
    
    if (showTime) {
      const timeStr = `${selectedTime.hours}:${selectedTime.minutes}`;
      formatted += ` at ${timeStr}`;
    }
    
    return formatted;
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    if (showTime) {
      newDate.setHours(parseInt(selectedTime.hours), parseInt(selectedTime.minutes));
    }
    
    setSelectedDate(newDate);
    onChange(newDate.toISOString());
    
    if (!showTime) {
      setIsOpen(false);
    }
  };

  const handleTimeChange = (type, value) => {
    const newTime = { ...selectedTime, [type]: value };
    setSelectedTime(newTime);
    
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(newTime.hours), parseInt(newTime.minutes));
      setSelectedDate(newDate);
      onChange(newDate.toISOString());
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    const today = new Date();
    if (showTime) {
      today.setHours(parseInt(selectedTime.hours), parseInt(selectedTime.minutes));
    }
    setSelectedDate(today);
    setCurrentDate(today);
    onChange(today.toISOString());
    if (!showTime) {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setSelectedDate(null);
    onChange('');
    setIsOpen(false);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Previous month's trailing days
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(
        <button
          key={`prev-${prevMonthDays - i}`}
          className="w-8 h-8 text-[10px] text-[rgba(255,255,255,0.3)] hover:text-[rgba(255,255,255,0.5)] transition-colors"
          onClick={() => {
            navigateMonth(-1);
            setTimeout(() => handleDateSelect(prevMonthDays - i), 0);
          }}
        >
          {prevMonthDays - i}
        </button>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === currentDate.getMonth() &&
        selectedDate.getFullYear() === currentDate.getFullYear();
      
      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          className={`w-8 h-8 text-[10px] transition-colors rounded ${
            isSelected
              ? 'text-white'
              : isToday
              ? `border`
              : 'text-white hover:bg-[rgba(255,255,255,0.1)]'
          }`}
          style={{
            backgroundColor: isSelected ? currentTheme.primary : isToday ? currentTheme.primaryBg : 'transparent',
            color: isSelected ? (theme === 'bookings' ? 'black' : 'white') : isToday ? currentTheme.primary : 'white',
            borderColor: isToday ? currentTheme.border : 'transparent'
          }}
        >
          {day}
        </button>
      );
    }

    // Next month's leading days
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    const remainingCells = totalCells - (firstDay + daysInMonth);
    
    for (let day = 1; day <= remainingCells; day++) {
      days.push(
        <button
          key={`next-${day}`}
          className="w-8 h-8 text-[10px] text-[rgba(255,255,255,0.3)] hover:text-[rgba(255,255,255,0.5)] transition-colors"
          onClick={() => {
            navigateMonth(1);
            setTimeout(() => handleDateSelect(day), 0);
          }}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
          {label.includes('*') ? (
            <>
              {label.replace(' *', '')}
              <span className={`ml-1`} style={{ color: currentTheme.primary }}>*</span>
            </>
          ) : (
            <>
              {label}
              {required && <span className={`ml-1`} style={{ color: currentTheme.primary }}>*</span>}
            </>
          )}
        </label>
      )}
      
      {/* Input Field */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] text-left outline-none transition-all flex items-center justify-between`}
        style={{ 
          '--focus-border-color': currentTheme.border,
          '--hover-border-color': currentTheme.border,
          '--hover-bg-color': currentTheme.primaryBg
        }}
        onMouseEnter={(e) => {
          e.target.style.borderColor = currentTheme.border;
          e.target.style.backgroundColor = currentTheme.primaryBg;
        }}
        onMouseLeave={(e) => {
          e.target.style.borderColor = 'rgba(255,255,255,0.1)';
          e.target.style.backgroundColor = 'rgba(255,255,255,0.05)';
        }}
        onFocus={(e) => e.target.style.borderColor = currentTheme.border}
        onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
      >
        <span className={selectedDate ? 'text-white' : 'text-[rgba(255,255,255,0.5)]'}>
          {formatDisplayDate(selectedDate)}
        </span>
        <svg className="w-4 h-4 text-[rgba(255,255,255,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Calendar */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-black border border-[rgba(255,255,255,0.2)] rounded-lg p-4 z-50 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-1 text-white transition-colors"
                style={{ '--hover-color': currentTheme.primary }}
                onMouseEnter={(e) => e.target.style.color = currentTheme.primary}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <h3 className="text-white text-[12px] font-medium">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              
              <button
                onClick={() => navigateMonth(1)}
                className="p-1 text-white transition-colors"
                style={{ '--hover-color': currentTheme.primary }}
                onMouseEnter={(e) => e.target.style.color = currentTheme.primary}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Days of Week */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {daysOfWeek.map(day => (
                <div key={day} className="text-center text-[rgba(255,255,255,0.5)] text-[10px] font-medium py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-4">
              {renderCalendarDays()}
            </div>

            {/* Time Picker */}
            {showTime && (
              <div className="border-t border-[rgba(255,255,255,0.1)] pt-4 mb-4">
                <div className="flex items-center justify-center gap-2">
                  <select
                    value={selectedTime.hours}
                    onChange={(e) => handleTimeChange('hours', e.target.value)}
                    className="bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded px-2 py-1 text-white text-[11px] focus:outline-none"
                    style={{ '--focus-border-color': currentTheme.border }}
                    onFocus={(e) => e.target.style.borderColor = currentTheme.border}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i.toString().padStart(2, '0')} className="bg-black">
                        {i.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <span className="text-white text-[11px]">:</span>
                  <select
                    value={selectedTime.minutes}
                    onChange={(e) => handleTimeChange('minutes', e.target.value)}
                    className="bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded px-2 py-1 text-white text-[11px] focus:outline-none"
                    style={{ '--focus-border-color': currentTheme.border }}
                    onFocus={(e) => e.target.style.borderColor = currentTheme.border}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i.toString().padStart(2, '0')} className="bg-black">
                        {i.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-between">
              <button
                onClick={handleClear}
                className="text-[11px] hover:opacity-70 transition-opacity"
                style={{ color: currentTheme.primary }}
              >
                Clear
              </button>
              <button
                onClick={handleToday}
                className="text-[11px] hover:opacity-70 transition-opacity"
                style={{ color: currentTheme.primary }}
              >
                Today
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomCalendar;

